import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
interface SelectOption {
  label: string;
  value: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label)
    .join(", ");

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-sm font-medium mb-1">{label}</label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
          if (open) {
            setSearchQuery("");
          }
        }}
        className={`w-full text-left px-3 py-2 border rounded-md bg-white
          ${error ? "border-red-500" : "border-gray-300"}
          ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
      >
        {selectedLabels || placeholder}
      </button>
      {open && (
        <div
          className="fixed z-[9999]
               mt-1 max-h-56 overflow-y-auto
               bg-white border border-gray-300
               rounded-md shadow-xl"
          style={{
            width: dropdownRef.current?.offsetWidth,
            top: dropdownRef.current?.getBoundingClientRect().bottom ?? 0,
            left: dropdownRef.current?.getBoundingClientRect().left ?? 0,
          }}
        >
          <div className="sticky top-0 bg-white border-b p-2 z-10">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              No users found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 px-3 py-2
                     hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}

    

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MultiSelectDropdown;
